import { Injectable } from '@angular/core';
import { ModalService } from '@assetaccountant/core/services/modal.service';

import { HttpClient } from '@angular/common/http';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { AuthorizationService } from '@assetaccountant/core/services/authorization.service';
import { environment } from '../../../../environments/environment';
import { ConnectIntegrationResponse } from '../models/common/connect-integration-response';
import { ConnectDynamicsCommand } from '../models/dynamics/connect-dynamics-command';
import { ListDynamicsCompaniesResponse } from '../models/dynamics/list-dynamics-companies.response';
import { ListDynamicsEnvironmentsResponse } from '../models/dynamics/list-dynamics-environments-response';
import { OidcStateParams } from '../models/oidc-state-params';

export const dynamicsConfig = {
  responseType: 'code',
  scope: environment.integrations.dynamics.scope,
  clientId: environment.integrations.dynamics.clientId,
  redirectUri: environment.integrations.dynamics.redirectUri,
  issuer: environment.integrations.dynamics.issuer,
};

@Injectable({
  providedIn: 'root',
})
export class MicrosoftDynamicsService {
  constructor(
    private http: HttpClient,
    private modalService: ModalService,
    private authorizationService: AuthorizationService,
  ) {}

  initiateOAuth(returnRoute: string) {
    const state = this.generateState(returnRoute);

    const url =
      dynamicsConfig.issuer +
      '?response_type=' +
      encodeURIComponent(dynamicsConfig.responseType) +
      '&client_id=' +
      encodeURIComponent(dynamicsConfig.clientId) +
      '&state=' +
      encodeURIComponent(state) +
      '&nonce=' +
      encodeURIComponent(state) +
      '&redirect_uri=' +
      encodeURIComponent(dynamicsConfig.redirectUri) +
      '&scope=' +
      encodeURIComponent(dynamicsConfig.scope);

    window.location.href = url;
  }

  generateState(returnRoute: string) {
    const stateParams = new OidcStateParams();
    stateParams.returnRoute = returnRoute;

    const state: string = btoa(JSON.stringify(stateParams));

    localStorage.setItem('aa-oidc-state', state);

    return state;
  }

  connect(command: ConnectDynamicsCommand) {
    return this.http.post<ConnectIntegrationResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations/microsoftDynamics`,
      command,
    );
  }

  listEnvironments(authorizationCode: string) {
    return this.http.get<ListDynamicsEnvironmentsResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations/microsoftDynamics/environments?authorizationCode=${encodeURIComponent(authorizationCode)}`,
    );
  }

  listCompanies(tenantId: string, environmentName: string) {
    return this.http.get<ListDynamicsCompaniesResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations/microsoftDynamics/companies?tenantId=${tenantId}&environment=${environmentName}`,
    );
  }

  handleError(registerId, httpError, returnRoute, metadata?) {
    const returnUrl = new URL(returnRoute, location.origin);

    if (!returnUrl.searchParams.has('environmentName') && metadata && metadata.EnvironmentName) {
      returnUrl.searchParams.append('environmentName', metadata.EnvironmentName);
    }

    if (!returnUrl.searchParams.has('companyId') && metadata && metadata.CompanyId) {
      returnUrl.searchParams.append('companyId', metadata.CompanyId);
    }

    if (
      httpError.status === 403 ||
      (Array.isArray(httpError.error) && httpError.error[0].message === 'invalid_grant')
    ) {
      if (
        registerId &&
        this.authorizationService.isRegisterAuthorized(
          registerId,
          RegisterPermissionType.CreateIntegrationConnection,
        )
      ) {
        this.modalService
          .showErrorModal(
            'We were unable to authenticate with Microsoft Dynamics, please reconnect and try again.',
            'Authentication Failure',
            'Reconnect',
          )
          .subscribe(_ => this.initiateOAuth(returnRoute));
      } else {
        this.modalService.showErrorModal(
          'We were unable to authenticate with Microsoft Dynamics, please ask an administrator to reconnect this register',
          'Authentication Failure',
          'OK',
        );
      }
    } else {
      throw httpError;
    }
  }
}
